import { Component } from '@theme/component';

/**
 * Recommends a size from a neck measurement using the Happy Beads "size up" rule.
 *
 * - Inside one or more size ranges (ranges overlap by design) -> size up, recommend the largest match.
 * - Below the smallest size -> nearest (smallest), flagged 'below'.
 * - Above the largest size -> nearest (largest), flagged 'above'.
 * - In a gap between ranges -> size up to the next size whose range starts above the measurement.
 *
 * @param {number} neckCm
 * @param {{ size: string, min: number, max: number }[]} sizes
 * @returns {{ size: string, note: '' | 'below' | 'above' } | null}
 */
export function recommendSize(neckCm, sizes) {
  const valid = sizes
    .filter((s) => s.size && Number.isFinite(s.min) && Number.isFinite(s.max))
    .sort((a, b) => a.min - b.min);

  if (!valid.length || !Number.isFinite(neckCm)) return null;

  const matches = valid.filter((s) => neckCm >= s.min && neckCm <= s.max);
  if (matches.length) {
    const chosen = matches.reduce((largest, current) => (current.max > largest.max ? current : largest));
    return { size: chosen.size, note: '' };
  }

  const smallest = valid[0];
  const largest = valid[valid.length - 1];

  if (neckCm < smallest.min) return { size: smallest.size, note: 'below' };
  if (neckCm > largest.max) return { size: largest.size, note: 'above' };

  const next = valid.find((s) => s.min > neckCm);
  return { size: (next ?? largest).size, note: '' };
}

/**
 * Sizing helper presented as an overlay (native <dialog>) opened from a trigger button or the
 * variant picker's "Size Chart" link — keeping the size guide out of the inline buy flow.
 * Recommends a size from a neck measurement and, on confirm, selects the matching variant by
 * reusing the theme's variant-picker change pipeline, then closes the overlay.
 */
class SizingHelper extends Component {
  #controller = new AbortController();

  /** @type {string | null} */
  #recommended = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;

    this.#input?.addEventListener('input', this.#handleInput, { signal });
    this.#slider?.addEventListener('input', this.#handleSlider, { signal });
    this.#selectButton?.addEventListener('click', this.#handleSelect, { signal });
    this.#trigger?.addEventListener('click', this.#handleOpen, { signal });
    this.#closeButton?.addEventListener('click', this.#handleClose, { signal });
    this.#dialog?.addEventListener('click', this.#handleBackdrop, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** @returns {HTMLInputElement | null} */
  get #input() {
    const ref = this.refs.input;
    return ref instanceof HTMLInputElement ? ref : null;
  }

  /** @returns {HTMLInputElement | null} */
  get #slider() {
    const ref = this.refs.slider;
    return ref instanceof HTMLInputElement ? ref : null;
  }

  /** @returns {HTMLElement | null} */
  get #result() {
    const ref = this.refs.result;
    return ref instanceof HTMLElement ? ref : null;
  }

  /** @returns {HTMLButtonElement | null} */
  get #selectButton() {
    const ref = this.refs.selectButton;
    return ref instanceof HTMLButtonElement ? ref : null;
  }

  /** @returns {HTMLDialogElement | null} */
  get #dialog() {
    const ref = this.refs.dialog;
    return ref instanceof HTMLDialogElement ? ref : null;
  }

  /** @returns {HTMLElement | null} */
  get #trigger() {
    const ref = this.refs.trigger;
    return ref instanceof HTMLElement ? ref : null;
  }

  /** @returns {HTMLElement | null} */
  get #closeButton() {
    const ref = this.refs.closeButton;
    return ref instanceof HTMLElement ? ref : null;
  }

  /** @returns {{ size: string, min: number, max: number, el: HTMLElement }[]} */
  get #sizes() {
    return Array.from(this.querySelectorAll('[data-size]')).map((el) => ({
      el: /** @type {HTMLElement} */ (el),
      size: el.getAttribute('data-size') ?? '',
      min: Number.parseFloat(el.getAttribute('data-min') ?? ''),
      max: Number.parseFloat(el.getAttribute('data-max') ?? ''),
    }));
  }

  #handleOpen = () => this.open();
  #handleClose = () => this.close();

  /** @param {Event} event */
  #handleBackdrop = (event) => {
    if (event.target === this.#dialog) this.close();
  };

  /** Opens the overlay, focuses the calculator, and tracks the open. */
  open() {
    const dialog = this.#dialog;
    if (!dialog) return;

    if (typeof dialog.showModal === 'function') {
      if (!dialog.open) dialog.showModal();
    } else {
      dialog.setAttribute('open', '');
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'sizing_helper_opened',
      item_id: this.dataset.productId || undefined,
    });

    const input = this.#input;
    if (input) window.requestAnimationFrame(() => input.focus({ preventScroll: true }));
  }

  close() {
    const dialog = this.#dialog;
    if (!dialog) return;
    if (typeof dialog.close === 'function' && dialog.open) dialog.close();
    else dialog.removeAttribute('open');
  }

  #handleInput = () => {
    const input = this.#input;
    const result = this.#result;
    const selectButton = this.#selectButton;
    if (!input) return;

    const rows = this.#sizes;
    rows.forEach((row) => row.el.removeAttribute('data-recommended'));

    const value = Number.parseFloat(input.value);
    const slider = this.#slider;
    if (slider && Number.isFinite(value)) slider.value = String(value);
    if (input.value.trim() === '' || !Number.isFinite(value)) {
      this.#recommended = null;
      if (result) {
        result.hidden = true;
        result.textContent = '';
      }
      if (selectButton) selectButton.hidden = true;
      return;
    }

    const recommendation = recommendSize(
      value,
      rows.map(({ size, min, max }) => ({ size, min, max }))
    );
    if (!recommendation) return;

    this.#recommended = recommendation.size;

    const matchedRow = rows.find((row) => row.size === recommendation.size);
    if (matchedRow) matchedRow.el.setAttribute('data-recommended', 'true');

    if (result) {
      const template =
        recommendation.note === 'below'
          ? this.dataset.resultBelow
          : recommendation.note === 'above'
          ? this.dataset.resultAbove
          : this.dataset.result;
      result.textContent = (template || 'Best fit: {size}').replace('{size}', recommendation.size);
      result.hidden = false;
    }

    if (selectButton) {
      const radio = this.#findRadio(recommendation.size);
      selectButton.hidden = !radio;
      selectButton.textContent = (this.dataset.selectLabel || 'Select size {size}').replace(
        '{size}',
        recommendation.size
      );
    }
  };

  #handleSlider = () => {
    const slider = this.#slider;
    const input = this.#input;
    if (!slider || !input) return;
    input.value = slider.value;
    this.#handleInput();
  };

  #handleSelect = () => {
    if (!this.#recommended) return;
    const radio = this.#findRadio(this.#recommended);
    if (!radio) return;

    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
    this.close();
  };

  /**
   * Finds the variant radio whose value matches a size name (case-insensitive).
   * @param {string} size
   * @returns {HTMLInputElement | null}
   */
  #findRadio(size) {
    const picker = document.querySelector('variant-picker');
    if (!picker) return null;

    const target = size.trim().toLowerCase();
    const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
    const match = radios.find((radio) => (radio.getAttribute('value') ?? '').trim().toLowerCase() === target);

    return match instanceof HTMLInputElement ? match : null;
  }
}

if (!customElements.get('sizing-helper')) {
  customElements.define('sizing-helper', SizingHelper);
}

if (typeof window !== 'undefined' && typeof window.openSizeHelper !== 'function') {
  /** Opens the inline sizing helper overlay, falling back to the legacy modal if the block isn't present. */
  window.openSizeHelper = () => {
    const helper = document.querySelector('sizing-helper');
    if (helper && typeof (/** @type {any} */ (helper).open) === 'function') {
      /** @type {any} */ (helper).open();
    } else if (typeof (/** @type {any} */ (window).openSizeChart) === 'function') {
      /** @type {any} */ (window).openSizeChart();
    }
  };
}
